import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import type { IMediaLibraryImageModel } from '@/shared/models/media-library/media-library-image.model';
import { useTranslation } from 'react-i18next';
import { useGetUserVideosQuery, useUploadVideoMutation } from '@/editor/services/http/media-library.api';
import { createVideoPayload } from '../utils/video.utils';
import { prepareVideoFileForUpload } from '../utils/upload.utils';
import { getValueFromLs } from '@/shared/services/localstorage';
import { EDITOR_LS_CONSTANTS } from '@/editor/shared/constants/editor-LS.constants';
import { useCreateCreatomateMutation } from '@/editor/services/http/creatomate.api';

export const useVideoCreation = () => {
  const [previewVideoUrl, setPreviewVideoUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [tabValue, setTabValue] = useState(1);

  const openPreviewModal = useCallback(() => {
    setPreviewModalOpen(true);
  }, []);

  const closePreviewModal = useCallback(() => {
    setPreviewModalOpen(false);
  }, []);

  const { t } = useTranslation();
  const propertyFromLS = getValueFromLs(EDITOR_LS_CONSTANTS.PROPERTY_INTERNAL_ID);

  const [createCreatomate] = useCreateCreatomateMutation();
  const [uploadVideo, { isLoading: isVideoLoading }] = useUploadVideoMutation();
  const { data, refetch: mediaLibraryVideosRefetch } = useGetUserVideosQuery();

  const generateVideo = useCallback(
    async (
      formData: any,
      propertyData: any,
      selectedImages: IMediaLibraryImageModel[],
      selectedTemplateId: string | null
    ) => {
      if (!selectedTemplateId) {
        toast.error(t('pleaseSelectTemplate'));
        return;
      }

      setPreviewLoading(true);
      setPreviewVideoUrl(null);

      const payload = createVideoPayload({ ...formData, selectedImages, selectedTemplateId }, propertyData);

      try {
        openPreviewModal();
        const response: any = await createCreatomate(payload).unwrap();
        setPreviewVideoUrl(response.url);
        if (response.status == 'failed') {
          closePreviewModal();
          toast.error('failed to generate video');
        }
        return response.url;
      } catch (error) {
        setPreviewVideoUrl(null);
        closePreviewModal();
        toast.error(t('somethingWrong'));
        console.error('Error creating video:', error);
        throw error;
      } finally {
        setPreviewLoading(false);
      }
    },
    [createCreatomate, t]
  );

  const uploadGeneratedVideo = useCallback(async () => {
    if (!previewVideoUrl || !propertyFromLS) return;

    try {
      const file = await prepareVideoFileForUpload(previewVideoUrl);

      await uploadVideo({
        propertyId: propertyFromLS,
        extension: file.extension,
        fileName: file.fileName,
        file: file.file,
      }).unwrap();

      toast.success(t('videoUploadedSuccessfully'));
      return true;
    } catch (error) {
      console.error('Error uploading video:', error);
      toast.error(t('failedToUploadVideo'));
      throw error;
    } finally {
      closePreviewModal();
      setTabValue(1);
      mediaLibraryVideosRefetch();
    }
  }, [previewVideoUrl, propertyFromLS, uploadVideo, t]);

  const resetVideoCreation = useCallback(() => {
    setPreviewVideoUrl(null);
    setPreviewLoading(false);
    setPreviewModalOpen(false);
  }, []);

  return {
    previewVideoUrl,
    previewLoading,
    isVideoLoading,
    generateVideo,
    uploadGeneratedVideo,
    resetVideoCreation,
    previewModalOpen,
    mediaLibraryVideosRefetch,
    data,
    setTabValue,
    tabValue,
  };
};
